import { CommonModule } from '@angular/common'
import { Component, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Subject, debounceTime, switchMap } from 'rxjs'
import { InternationalizationService } from '../../../../../services/internationalization.service'
import { ChatEntity, UserSearchResult } from '../../../interfaces/chat.interface'
import { MessageChatStateService } from '../../../services/message-chat-state.service'
import { MessageChatService } from '../../../services/message-chat.service'

@Component({
  selector: 'app-edit-group-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './edit-group-modal.component.html',
  styleUrl: './edit-group-modal.component.scss',
})
export class EditGroupModalComponent {
  private readonly searchSubject = new Subject<string>()

  readonly isOpen = signal(false)
  readonly chatId = signal<number | null>(null)
  readonly groupName = signal('')
  readonly searchText = signal('')
  readonly searchResults = signal<UserSearchResult[]>([])
  readonly selectedMembers = signal<UserSearchResult[]>([])
  private originalMemberIds: number[] = []

  readonly save = output<{
    chatId: number
    name: string
    addedUserIds: number[]
    removedUserIds: number[]
  }>()

  constructor(
    private chatService: MessageChatService,
    private state: MessageChatStateService,
    protected translateService: InternationalizationService
  ) {
    this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((term) => this.chatService.findActiveUsers(term)),
        takeUntilDestroyed()
      )
      .subscribe((users) => {
        const currentId = this.state.currentUserId()
        const selectedIds = this.selectedMembers().map((m) => m.userId)
        this.searchResults.set(
          users.filter((u) => u.userId !== currentId && !selectedIds.includes(u.userId))
        )
      })
  }

  open(chat: ChatEntity): void {
    this.isOpen.set(true)
    this.chatId.set(chat.id)
    this.groupName.set(chat.name)
    this.searchText.set('')
    this.searchResults.set([])

    const currentId = this.state.currentUserId()
    const members = chat.participants
      .filter((p) => p.userId !== currentId)
      .map((p) => ({ userId: p.userId, userName: p.userName }))

    this.selectedMembers.set(members)
    this.originalMemberIds = members.map((m) => m.userId)
  }

  close(): void {
    this.isOpen.set(false)
  }

  onSearchChange(value: string): void {
    this.searchText.set(value)
    if (value.trim().length >= 2) {
      this.searchSubject.next(value)
    } else {
      this.searchResults.set([])
    }
  }

  selectUser(user: UserSearchResult): void {
    if (!this.selectedMembers().find((m) => m.userId === user.userId)) {
      this.selectedMembers.update((members) => [...members, user])
    }
    this.searchText.set('')
    this.searchResults.set([])
  }

  removeMember(userId: number): void {
    this.selectedMembers.update((members) => members.filter((m) => m.userId !== userId))
  }

  onSubmit(): void {
    const name = this.groupName().trim()
    const id = this.chatId()
    if (!name || !id) {
      return
    }

    const currentIds = this.selectedMembers().map((m) => m.userId)
    const addedUserIds = currentIds.filter((id) => !this.originalMemberIds.includes(id))
    const removedUserIds = this.originalMemberIds.filter((id) => !currentIds.includes(id))

    this.save.emit({ chatId: id, name, addedUserIds, removedUserIds })
    this.close()
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.close()
    }
  }
}
